import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShoppingCartIcon, MapPinIcon, ChevronDownIcon, HeartIcon, TrashIcon, XIcon, ChevronRightIcon, TrendingUpIcon, ShoppingBagIcon, TagIcon, HeartPulseIcon, ActivityIcon, BrainIcon, ThermometerIcon, PillIcon, StethoscopeIcon, GlobeIcon, UserIcon, PackageIcon, LogOutIcon, ClockIcon, MenuIcon, ScanBarcodeIcon, BellIcon } from 'lucide-react';
import { LoyaltyCard } from './account/LoyaltyCard';
import { MobileExperienceSwitcher } from './home/MobileExperienceSwitcher';
import { LoyaltyOnboardingBottomSheet } from './modals/LoyaltyOnboardingBottomSheet';
import { AuthBottomSheet } from './modals/AuthBottomSheet';
import { LanguageBottomSheet } from './modals/LanguageBottomSheet';
import { useStore } from '../lib/store';
import { POPULAR_SEARCHES, TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../data/searchData';
// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}
interface MobileHomeHeaderProps {
  selectedLocation: {
    name: string;
  } | null;
  onOpenLocationModal: () => void;
  alwaysWhiteBackground?: boolean;
  transparentAtTop?: boolean;
  showExperienceSwitcher?: boolean;
  activeExperience?: 'shop' | 'innova' | 'rx';
}
export function MobileHomeHeader({
  selectedLocation,
  onOpenLocationModal,
  alwaysWhiteBackground = false,
  transparentAtTop = false,
  showExperienceSwitcher = false,
  activeExperience
}: MobileHomeHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    cartCount,
    cartTotal,
    language,
    setLanguage,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const currentCartCount = cartCount();
  const currentWishlistCount = wishlistItems.length;
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResultsLimit, setSearchResultsLimit] = useState(5);
  const cartMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // Check if selected location is a pickup branch
  const isPickupLocation = selectedLocation && 'distance' in selectedLocation;
  // States for bottom sheets
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  // Calculate loyalty progress from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const openAuthModal = () => {
    setAuthBottomSheetOpen(true);
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const handleSearchFocus = () => {
    setSearchFocused(true);
    setShowSearchResults(true);
  };
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, [setSearchQuery]);
  useEffect(() => {
    if (debouncedSearchQuery) {
      setShowSearchResults(true);
    }
  }, [debouncedSearchQuery]);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchFocused(false);
    }
  };
  const handleSearchSelect = (query: string) => {
    setSearchQuery(query);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const currentCartTotal = cartTotal();
  const conditions = [{
    icon: <HeartPulseIcon className="w-4 h-4 text-red-600" data-id="element-3164" data-mp="vusiwpeenaj0" />,
    name: 'Heart Health',
    slug: 'heart-health'
  }, {
    icon: <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-3165" data-mp="qvzernl0cvvi" />,
    name: 'Diabetes',
    slug: 'diabetes'
  }, {
    icon: <BrainIcon className="w-4 h-4 text-purple-600" data-id="element-3166" data-mp="lvuudmgzbh00" />,
    name: 'Mental Health',
    slug: 'mental-health'
  }, {
    icon: <ThermometerIcon className="w-4 h-4 text-orange-600" data-id="element-3167" data-mp="gzcw95g5mgwn" />,
    name: 'Pain Relief',
    slug: 'pain-relief'
  }, {
    icon: <PillIcon className="w-4 h-4 text-green-600" data-id="element-3168" data-mp="2bc2nfff51c0c" />,
    name: 'Digestive Health',
    slug: 'digestive-health'
  }, {
    icon: <StethoscopeIcon className="w-4 h-4 text-cyan-600" data-id="element-3169" data-mp="27ah5vgrns9jt" />,
    name: 'Respiratory',
    slug: 'respiratory'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartMenuRef.current && !cartMenuRef.current.contains(event.target as Node)) {
        setShowCartMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        // Only close if not focused on input
        if (!searchFocused && !searchQuery) {
          setShowSearchResults(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchFocused, searchQuery]);
  const isSearchActive = showSearchResults || searchFocused;
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = isSearchActive ? 'hidden' : 'auto';
    }
    return () => {
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isSearchActive]);
  const handleCategoryClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  const handleConditionClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  return <>
      <div className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-200 m-0 ${alwaysWhiteBackground ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : isScrolled ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : transparentAtTop ? isSearchActive ? 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]' : 'bg-transparent' : 'bg-white border-b border-gray-100 shadow-[0_2px_6px_rgba(30,43,85,0.035)]'}`} style={{
      paddingTop: 'env(safe-area-inset-top)'
    }} data-id="element-3170" data-mp="12x5xze77r4yw">
        {!isSearchActive && <div className={`flex items-center gap-2 px-4 transition-[padding] duration-200 ${isScrolled ? 'py-1.5' : 'pb-1 pt-2'}`} data-id="element-3171" data-mp="2gr3756s0bzj2">
            <button type="button" onClick={onOpenLocationModal} className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-white px-3 text-left text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label="Change delivery location" data-id="element-3172" data-mp="26u890oznc15b">
              <MapPinIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} data-id="element-3173" data-mp="22u6b8qx17rvb" />
              <span className="min-w-0 flex-1 truncate text-sm" data-id="element-3174" data-mp="3s7jjgr89m01p">
                <span className="font-medium text-gray-600" data-id="element-3175" data-mp="3s68ewp6zwo9j">{isPickupLocation ? 'Pickup: ' : 'Deliver to: '}</span>
                <span className="font-bold text-gray-900" data-id="element-3176" data-mp="3ltdt7ve2fl3b">{selectedLocation?.name || 'Select Location'}</span>
              </span>
              <ChevronDownIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} data-id="element-3177" data-mp="3qkvu6jg39loa" />
            </button>
            <button type="button" onClick={() => setSearchFocused(true)} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label="Search" data-id="element-3178" data-mp="3ctgxj9t9dlyr">
              <SearchIcon className="h-5 w-5" strokeWidth={2} data-id="element-3179" data-mp="dc0wfpy2vykh" />
            </button>
            <button type="button" onClick={() => navigate('/menu')} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label="Open menu" data-id="element-3180" data-mp="3vjqe5opslg9z">
              <MenuIcon className="h-5 w-5" strokeWidth={2} data-id="element-3181" data-mp="2p3wd0mhgignd" />
            </button>
            <button type="button" onClick={() => navigate('/cart')} className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2B55] shadow-[0_1px_3px_rgba(30,43,85,0.06)]" aria-label={`Cart, ${currentCartCount} items`} data-id="element-3182" data-mp="2glb64o6pv6hk">
              <ShoppingCartIcon className="h-5 w-5" strokeWidth={2} data-id="element-3183" data-mp="2llgrxnqb1yrq" />
              {currentCartCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-id="element-3184" data-mp="1xta1t6b6ao0r">
                  {currentCartCount}
                </span>}
            </button>
          </div>}
        {isSearchActive && <div className={`flex items-center gap-2.5 px-4 transition-all duration-300 ${isSearchActive ? 'py-2' : alwaysWhiteBackground ? 'pb-2' : transparentAtTop ? showExperienceSwitcher ? 'pb-1' : 'pb-3' : 'py-3'}`} data-id="element-3185" data-mp="22smqlwllu55z">
          {isSearchActive && <button onClick={() => {
          setShowSearchResults(false);
          setSearchFocused(false);
        }} className="p-1 -ml-1 text-gray-500 hover:text-gray-700" data-id="element-3186" data-mp="3a7rqx1yi556t">
              <ChevronRightIcon className="w-6 h-6 rotate-180" data-id="element-3187" data-mp="3nz6ivyae0gq0" />
            </button>}
          <div className="flex-1 relative" ref={searchRef} data-id="element-3188" data-mp="2r9fdv54gzb5y">
            <form onSubmit={handleSearchSubmit} data-id="element-3189" data-mp="2rb09ky2it05e">
              <div className={`relative rounded-lg transition-all flex items-center bg-white border border-gray-200 shadow-sm`} data-id="element-3190" data-mp="2w7qpnlebrvqt">
                <SearchIcon className="absolute left-3 text-gray-400 w-5 h-5 pointer-events-none z-10" data-id="element-3191" data-mp="30bygbo4cflro" />
                <input type="text" placeholder="Search Personal Care" value={searchQuery} onChange={e => handleSearchChange(e.target.value)} onFocus={handleSearchFocus} className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none bg-transparent" data-id="element-3192" data-mp="356kh6xcstvxi" />
                {searchQuery ? <button type="button" onClick={() => {
                setSearchQuery('');
              }} className="absolute right-3 text-gray-400 hover:text-gray-600 z-10" data-id="element-3193" data-mp="kvij3c6md4l9">
                    <XIcon className="w-5 h-5" data-id="element-3194" data-mp="6wiuu5n0qrpm" />
                  </button> : <button type="button" className="absolute right-3 text-[#1E2B55] hover:text-blue-700 z-10" data-id="element-3195" data-mp="1xfngejkg9eh">
                    <ScanBarcodeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-3196" data-mp="3u44e7wzyjori" />
                  </button>}
              </div>
            </form>
          </div>
          {!isSearchActive && <>
              <button type="button" onClick={() => navigate('/menu')} aria-label="Open menu" className={`rounded-lg bg-white hover:bg-gray-50 transition-all flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-3197" data-mp="1zduv2tthr5q4">
                <MenuIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-3198" data-mp="1ud73a5xfukuu" />
              </button>
              <div className="relative flex-shrink-0" ref={cartMenuRef} data-id="element-3199" data-mp="1gmbuetmxw3r9">
                <button onClick={() => setShowCartMenu(!showCartMenu)} className={`rounded-lg bg-white hover:bg-gray-50 transition-all relative flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-3200" data-mp="1le3ld5a0cbhx">
                  <ShoppingCartIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-3201" data-mp="xnzhcwswq9qe" />
                  {currentCartCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm" data-id="element-3202" data-mp="j10tfbt9dxgf">
                      {currentCartCount}
                    </span>}
                </button>
                {showCartMenu && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-id="element-3203" data-mp="nzkfrdbrlb9b">
                    <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center" data-id="element-3204" data-mp="11yk6qrmr228n">
                      <div data-id="element-3205" data-mp="wz7hk08eoj2w">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3206" data-mp="1bpy9d89zzi60">
                          Shopping Cart
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-3207" data-mp="16sfy3b8icquu">
                          {cartItems.reduce((total, item) => total + item.quantity, 0)}{' '}
                          items
                        </p>
                      </div>
                      <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/cart');
                }} className="text-xs font-medium text-[#1E2B55] hover:underline" data-id="element-3208" data-mp="dckas4l8hl03">
                        View Cart
                      </button>
                    </div>
                    {cartItems.length > 0 ? <>
                        <div className="py-2 max-h-80 overflow-y-auto" data-id="element-3209" data-mp="112en8l1n57jt">
                          {cartItems.map(item => <div key={item.id} className="flex items-center px-4 py-3 hover:bg-gray-50" data-id="element-3210" data-mp="160ye8zv3d9j1">
                              <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md overflow-hidden" data-id="element-3211" data-mp="37wihr4722xqf">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-3212" data-mp="3bxu5t5aa7awi" />
                              </div>
                              <div className="ml-3 flex-1 min-w-0" data-id="element-3213" data-mp="2umyiv91yt11j">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-3214" data-mp="2qm4uyfoivm8n">
                                  {item.name}
                                </p>
                                <div className="flex items-center mt-1" data-id="element-3215" data-mp="3ibrgd9y8jgy9">
                                  <p className="text-sm font-bold text-[#1E2B55]" data-id="element-3216" data-mp="3iag8oz1o5p1v">
                                    SAR {item.price.toFixed(2)}
                                  </p>
                                  <span className="text-xs text-gray-500 mx-1" data-id="element-3217" data-mp="39dp5m2op99xs">
                                    ×
                                  </span>
                                  <p className="text-xs text-gray-500" data-id="element-3218" data-mp="1xvebhcsgvxfa">
                                    {item.quantity}
                                  </p>
                                </div>
                              </div>
                              <div className="ml-2 flex-shrink-0" data-id="element-3219" data-mp="5w7jya2iavzd">
                                <button className="p-1 rounded-full text-gray-400 hover:text-red-500" data-id="element-3220" data-mp="3o1j67yvmozgs">
                                  <TrashIcon className="w-4 h-4" data-id="element-3221" data-mp="3t0dpf1xicq1f" />
                                </button>
                              </div>
                            </div>)}
                        </div>
                        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg" data-id="element-3222" data-mp="pkygk40v8mhv">
                          <div className="flex justify-between items-center mb-3" data-id="element-3223" data-mp="arv94pgznp4v">
                            <span className="text-sm font-medium text-gray-600" data-id="element-3224" data-mp="fr7z3j35pcbo">
                              Total
                            </span>
                            <span className="text-lg font-bold text-[#1E2B55]" data-id="element-3225" data-mp="189vfwmb4e56y">
                              {currentCartTotal.toFixed(2)} SAR
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2" data-id="element-3226" data-mp="2o1h2il62w82n">
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/cart');
                    }} className="text-center py-2 px-4 border border-[#1E2B55] text-[#1E2B55] font-medium rounded-md hover:bg-blue-50 transition-colors text-sm" data-id="element-3227" data-mp="31s2o4hmx2w48">
                              View Cart
                            </button>
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/checkout');
                    }} className="text-center py-2 px-4 bg-[#1E2B55] text-white font-medium rounded-md hover:bg-[#162041] transition-colors text-sm" data-id="element-3228" data-mp="2e2hw798scoit">
                              Checkout
                            </button>
                          </div>
                        </div>
                      </> : <div className="py-8 text-center" data-id="element-3229" data-mp="3k7dn8ybd8ytv">
                        <ShoppingCartIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3230" data-mp="3k5sthawvljx2" />
                        <p className="text-gray-500" data-id="element-3231" data-mp="20g2adozjkw8">Your cart is empty</p>
                        <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/');
                }} className="mt-3 inline-block text-sm font-medium text-[#1E2B55] hover:underline" data-id="element-3232" data-mp="3t8tzzcpnld19">
                          Continue Shopping
                        </button>
                      </div>}
                  </div>}
              </div>
            </>}
          </div>}
        {showExperienceSwitcher && !isSearchActive && <MobileExperienceSwitcher isCompact={isScrolled} activeExperience={activeExperience ?? (location.pathname.startsWith('/rx') ? 'rx' : 'shop')} onSelect={experience => {
        if (experience === 'rx') navigate('/rx');
        if (experience === 'innova') navigate('/now');
        if (experience === 'shop') navigate('/');
      }} data-id="element-3233" data-mp="14cr2j7f9clcq" />}
        <AnimatePresence data-id="element-3234" data-mp="lghh1z210sip">
          {showSearchResults && (searchFocused || searchQuery) && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-x-0 top-[56px] bottom-0 bg-white z-50 overflow-y-auto pb-20" style={{
          top: 'calc(56px + env(safe-area-inset-top))'
        }} data-id="element-3235" data-mp="7pmc2xunevke">
              <div className="p-4 space-y-6" data-id="element-3236" data-mp="1zmm4djlabvqo">
                {!searchQuery && recentSearches.length > 0 && <div data-id="element-3237" data-mp="1vkr32f3cticw">
                    <div className="flex items-center justify-between mb-3" data-id="element-3238" data-mp="1vm280i8jcw5l">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-3239" data-mp="1qnin8l5ohqet">
                        Recent Searches
                      </h3>
                      <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] font-bold" data-id="element-3240" data-mp="2jb4z0gp281cr">
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-3241" data-mp="29f1p2onyuyf9">
                      {recentSearches.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="flex items-center px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700" data-id="element-3242" data-mp="xxs94c41qoip" data-mpi={index}>
                          <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-3243" data-mp="sqtz309af6io" />
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && <div data-id="element-3244" data-mp="16jtfr5kt36rt">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3245" data-mp="16mpeux8e41hu">
                      Popular Searches
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-3246" data-mp="1gju270dz2308">
                      {POPULAR_SEARCHES.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="px-4 py-2 bg-blue-50 text-[#1E2B55] rounded-full text-xs font-bold" data-id="element-3247" data-mp="1lfj61fx0y62c" data-mpi={index}>
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && TRENDING_PRODUCTS.length > 0 && <div data-id="element-3248" data-mp="1qja99vmkpfzh">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3249" data-mp="1lma0hcyc9z4o">
                      Suggested Products
                    </h3>
                    <div className="space-y-3" data-id="element-3250" data-mp="xwyyjs595yoy">
                      {TRENDING_PRODUCTS.map(product => <button key={product.id} onClick={() => handleProductClick(product.id)} className="flex items-center w-full text-left p-3 bg-gray-50 rounded-2xl transition-colors" data-id="element-3251" data-mp="tt0zfhfxgnvw">
                          <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100" data-id="element-3252" data-mp="f4egz6v07g0g">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3253" data-mp="2luhxgitqls6f" />
                          </div>
                          <div className="ml-3 flex-1 min-w-0" data-id="element-3254" data-mp="2cvmq33eyic1j">
                            <p className="text-sm font-bold text-gray-900 truncate" data-id="element-3255" data-mp="2hs3fs21t0ygq">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-3256" data-mp="35gmi80y6ah0w">
                              {product.category}
                            </p>
                            <p className="text-sm font-black text-[#1E2B55] mt-1" data-id="element-3257" data-mp="2vjhye7rsuxn9">
                              {product.price.toFixed(2)} SAR
                            </p>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" data-id="element-3258" data-mp="1k3jipl249dd2" />
                        </button>)}
                    </div>
                  </div>}
                {searchQuery && <div data-id="element-3259" data-mp="110rcmqron0hc">
                    <div className="flex items-center justify-between mb-4" data-id="element-3260" data-mp="111kg9ivch9v9">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-3261" data-mp="w99d875b57yh">
                        Results for "{searchQuery}"
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3262" data-mp="r8ve5elop8cl">
                        {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length}{' '}
                        items found
                      </p>
                    </div>
                    <div className="space-y-3" data-id="element-3263" data-mp="27nn13c029nei">
                      {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, searchResultsLimit).map(product => <button key={product.id} onClick={() => handleProductClick(product.id)} className="flex items-center w-full text-left p-3 bg-gray-50 rounded-2xl transition-colors" data-id="element-3264" data-mp="2jyxhocqvommq">
                            <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100" data-id="element-3265" data-mp="1w8tgsd52r0zf">
                              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3266" data-mp="217eeo5wut8kp" />
                            </div>
                            <div className="ml-3 flex-1 min-w-0" data-id="element-3267" data-mp="3goewbh7u9j0k">
                              <p className="text-sm font-bold text-gray-900 truncate" data-id="element-3268" data-mp="6lchfkftoywn">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-3269" data-mp="1kepig50gqhy">
                                {product.category}
                              </p>
                              <p className="text-sm font-black text-[#1E2B55] mt-1" data-id="element-3270" data-mp="bjnr9v64b9ev">
                                {product.price.toFixed(2)} SAR
                              </p>
                            </div>
                            <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" data-id="element-3271" data-mp="kc7kuh7jynsu" />
                          </button>)}
                    </div>
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length > searchResultsLimit && <div className="mt-6" data-id="element-3272" data-mp="2w4wcus0u4jk7">
                        <button onClick={() => setSearchResultsLimit(searchResultsLimit + 5)} className="w-full py-3 bg-gray-100 text-gray-900 font-bold rounded-xl text-sm" data-id="element-3273" data-mp="1blk4zs5ei8ib">
                          Show More Results
                        </button>
                      </div>}
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && <div className="py-6 px-2" data-id="element-3274" data-mp="nuoh3642fxgc">
                        <div className="flex items-start gap-3 mb-4" data-id="element-3275" data-mp="nvgd2i1krx75">
                          <div className="w-12 h-12 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-3276" data-mp="2d9l8b4scaus7">
                            <span className="text-2xl" data-id="element-3277" data-mp="2i8fp616qy99o">🧑‍⚕️</span>
                          </div>
                          <div data-id="element-3278" data-mp="3k26oa7odf9y2">
                            <h3 className="text-base font-bold text-gray-900" data-id="element-3279" data-mp="3jyr8f2mvhwvl">
                              No Matching Search Results
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-3280" data-mp="3tx6ugr71mfy8">
                              The product doesn't seem to be available right
                              now. Don't worry — you can try other keywords, or
                              contact our pharmacist for help.
                            </p>
                          </div>
                        </div>
                        <ul className="text-sm text-gray-600 space-y-1.5 mb-5 pl-5 list-disc" data-id="element-3281" data-mp="gl6lnmd8nrhp">
                          <li data-id="element-3282" data-mp="lhww9u69xuvq">
                            Make sure the product name is spelled correctly.
                          </li>
                          <li data-id="element-3283" data-mp="2idcjo9l8sqdf">Try a shorter or more general keyword.</li>
                        </ul>
                        <div className="border-t border-gray-200 pt-4 mb-5" data-id="element-3284" data-mp="2nc61ufbh55jx">
                          <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-3285" data-mp="2fs4ux5pt8z1y">
                            Contact Customer Service / Ask a Pharmacist
                          </h4>
                          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3" data-id="element-3286" data-mp="261senpp13s1m">
                            <div className="w-9 h-9 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-3287" data-mp="2138plgf52051">
                              <span className="text-lg" data-id="element-3288" data-mp="2yowk1vs1a8dx">💬</span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3289" data-mp="2trd0cgckh3el">
                              <p className="text-sm font-semibold text-gray-900" data-id="element-3290" data-mp="2oqgu3w4qt560">
                                Need quick help?
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-3291" data-mp="1dcbokyovsn8k">
                                Chat with an Innova pharmacist or leave us your
                                question.
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3" data-id="element-3292" data-mp="byrytk5zb71">
                            <a href="https://wa.me/966500000000" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3293" data-mp="4e3dt64df3j4">
                              WhatsApp
                            </a>
                            <a href="tel:+966500000000" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3294" data-mp="33w32onbvu9jc">
                              Direct Call
                            </a>
                          </div>
                        </div>
                      </div>}
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Legacy sidebar disabled: mobile navigation now opens the /menu page. */}
      <AnimatePresence data-id="element-3295" data-mp="2kat4iqi11kqm">
        {false && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={() => setShowCategoriesMenu(false)} data-id="element-3296" data-mp="2y7g1uudw6cx2" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-0 w-full min-h-[100dvh] bg-white z-[70] shadow-xl overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]" data-id="element-3297" data-mp="3hw8kxan2chmq">
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white" data-id="element-3298" data-mp="1oybhhbw6rwbz">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="Innova Pharmacy" className="h-10" data-id="element-3299" data-mp="rdp9dx5rbx04" />
                <button onClick={() => setShowCategoriesMenu(false)} className="p-2 rounded-full hover:bg-gray-100" data-id="element-3300" data-mp="1695506lzk0ym">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-3301" data-mp="2coxoczugfake" />
                </button>
              </div>
              <div className="pb-[max(8rem,env(safe-area-inset-bottom))]" data-id="element-3302" data-mp="2hmh4646svveg">
                <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-3303" data-mp="5znbwh3ap9sv">
                  <div className="flex items-center space-x-3" data-id="element-3304" data-mp="11lr9rq812fa">
                    <div className="w-10 h-10 rounded-full bg-[#1E2B55] flex items-center justify-center text-white" data-id="element-3305" data-mp="1480dvsrfpzm">
                      <UserIcon className="w-5 h-5" data-id="element-3306" data-mp="3nhpxv7e1fogg" />
                    </div>
                    <div data-id="element-3307" data-mp="3nflwaw3m31jc">
                      <p className="text-sm font-medium" data-id="element-3308" data-mp="3iiv8tnph93nw">Hello, Guest</p>
                      <div className="flex space-x-2 mt-1" data-id="element-3309" data-mp="3ig0qq2txruxn">
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-3310" data-mp="3ef6zp0l7600j">
                          Sign In
                        </button>
                        <span className="text-xs text-gray-400" data-id="element-3311" data-mp="17qkatbem08o1">|</span>
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-3312" data-mp="12r7n6ofwyxke">
                          Register
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3313" data-mp="2nb31o8vjsal0">
                  <button onClick={() => setLanguageBottomSheetOpen(true)} className="flex items-center justify-between w-full" data-id="element-3314" data-mp="2s7jrr8mb4z0q">
                    <div className="flex items-center space-x-2" data-id="element-3315" data-mp="3kxjwn7gt6c3l">
                      <GlobeIcon className="w-4 h-4 text-gray-500" data-id="element-3316" data-mp="3kunxjft85hdk" />
                      <span className="text-sm" data-id="element-3317" data-mp="35z7ysxhp8xau">Language</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded" data-id="element-3318" data-mp="3axj9tbjqddun">
                      {language === 'en' ? 'English' : 'Arabic'}
                      <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-3319" data-mp="1zk756yg5cr7f" />
                    </div>
                  </button>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3320" data-mp="hkhky6cqi429">
                  <LoyaltyCard progress={loyaltyProgress} onJoinNow={() => {
                setShowCategoriesMenu(false);
                setLoyaltyBottomSheetOpen(true);
              }} loyaltyData={loyaltyData} data-id="element-3321" data-mp="cnr3bela70eq" />
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3322" data-mp="3v1gqo91ioko3">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3323" data-mp="3q2ndkhq56vog">MENU</h3>
                  <ul className="space-y-1" data-id="element-3324" data-mp="7nml9k4q5xme">
                    <li className="py-1" data-id="element-3325" data-mp="7oo62gbjlyhb">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/categories');
                  }} className="group w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold px-4 py-3 rounded-lg shadow-md text-center flex items-center justify-center gap-2" data-id="element-3326" data-mp="2x671xt2y7yz">
                        All Categories
                      </button>
                    </li>
                    <li data-id="element-3327" data-mp="916akr7o78l9">
                      <button onClick={() => handleCategoryClick('best-sellers')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3328" data-mp="90o7bamlbu3u">
                        <TrendingUpIcon className="w-4 h-4 mr-3" data-id="element-3329" data-mp="nyq7dauexxgc" />
                        <span className="text-sm" data-id="element-3330" data-mp="nwvr49u36til">Best Sellers</span>
                        <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-3331" data-mp="sqhsh5usvaz3">
                          HOT
                        </span>
                      </button>
                    </li>
                    <li data-id="element-3332" data-mp="11ry39epj1f3u">
                      <button onClick={() => handleCategoryClick('new-arrivals')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3333" data-mp="16p7oei5vv4lv">
                        <ShoppingBagIcon className="w-4 h-4 mr-3" data-id="element-3334" data-mp="1bta6cmbh6s5d" />
                        <span className="text-sm" data-id="element-3335" data-mp="16ufc8o825h5y">New Arrivals</span>
                        <span className="ml-auto text-xs bg-[#1E2B55] text-white px-1.5 py-0.5 rounded-full" data-id="element-3336" data-mp="16ws4pvbfz9x9">
                          NEW
                        </span>
                      </button>
                    </li>
                    <li data-id="element-3337" data-mp="1ukt9ct5i267e">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/offers');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3338" data-mp="1ulkt96pqifhu">
                        <TagIcon className="w-4 h-4 mr-3" data-id="element-3339" data-mp="2ic8aiiljihh2" />
                        <span className="text-sm" data-id="element-3340" data-mp="2dilymsn7ahmb">Deals & Offers</span>
                        <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-3341" data-mp="cskld79svbwl">
                          SALE
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3342" data-mp="2zvb2fpzk42u">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3343" data-mp="3v59003whslre">
                    SHOP BY CATEGORY
                  </h3>
                  <ul className="space-y-1" data-id="element-3344" data-mp="3q4uryluw5btd">
                    <li data-id="element-3345" data-mp="3l7l1d2vrmu3y">
                      <button onClick={() => handleCategoryClick('medicines')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3346" data-mp="3l69o8cgfka06">
                        <span className="text-sm" data-id="element-3347" data-mp="1eh4tr01ais33">Medicines (OTC)</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3348" data-mp="1efahsb7zyab5" />
                      </button>
                    </li>
                    <li data-id="element-3349" data-mp="2sdoa2r52c04y">
                      <button onClick={() => handleCategoryClick('vitamins-supplements')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3350" data-mp="2se7qg3f2eggv">
                        <span className="text-sm" data-id="element-3351" data-mp="363rt798nbdq6">Vitamins & Supplements</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3352" data-mp="29f3pzj1l9v0t" />
                      </button>
                    </li>
                    <li data-id="element-3353" data-mp="2eeg8xspcahy4">
                      <button onClick={() => handleCategoryClick('personal-care')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3354" data-mp="2efrbxqaynlo6">
                        <span className="text-sm" data-id="element-3355" data-mp="3tv6wb488xz2h">Personal Care</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3356" data-mp="2o44qbohxqrq" />
                      </button>
                    </li>
                    <li data-id="element-3357" data-mp="1yqhab87t9noo">
                      <button onClick={() => handleCategoryClick('mother-baby')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3358" data-mp="1ypo0tzhgwk2u">
                        <span className="text-sm" data-id="element-3359" data-mp="23qlimaykfc3t">Mother & Baby</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3360" data-mp="23o92828gh9sv" />
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3361" data-mp="1b2ze90hik3fw">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3362" data-mp="1pqasxb8zs3k8">
                    BROWSE BY CONDITION
                  </h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-3363" data-mp="1kz2hk9o3q84f">
                    {conditions.map((condition, index) => <button key={index} onClick={() => handleConditionClick(condition.slug)} className="inline-flex items-center gap-1.5 border border-gray-200 bg-gray-50 text-sm text-gray-800 px-3 py-2 rounded-full whitespace-nowrap" data-id="element-3364" data-mp="x8f8vm6d3egt" data-mpi={index}>
                        <span className="flex-shrink-0" data-id="element-3365" data-mp="2uy3ub72bwx63">{condition.icon}</span>
                        <span className="font-medium text-xs" data-id="element-3366" data-mp="2uw0u5u7gpnnn">
                          {condition.name}
                        </span>
                      </button>)}
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3367" data-mp="3romksi84qk2p">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3368" data-mp="3rlqnmw43vzfb">
                    MY ACCOUNT
                  </h3>
                  <ul className="space-y-1" data-id="element-3369" data-mp="3in6d4i8p8w26">
                    <li data-id="element-3370" data-mp="3ilv7sdzlvg8y">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3371" data-mp="3ip99dk0g3lfc">
                        <UserIcon className="w-4 h-4 mr-3" data-id="element-3372" data-mp="2hqy0jlicj98t" />
                        <span className="text-sm" data-id="element-3373" data-mp="2cs34hhvdbo6r">My Account</span>
                      </button>
                    </li>
                    <li data-id="element-3374" data-mp="28r1elrnccohg">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/orders');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3375" data-mp="28rjbmqhtv6qf">
                        <PackageIcon className="w-4 h-4 mr-3" data-id="element-3376" data-mp="2rgzn28v1wjlm" />
                        <span className="text-sm" data-id="element-3377" data-mp="2rghf4ez10gxv">My Orders</span>
                      </button>
                    </li>
                    <li data-id="element-3378" data-mp="1kyuf4jmbk1dt">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/wishlist');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3379" data-mp="1g23v5l7e8ln4">
                        <HeartIcon className="w-4 h-4 mr-3" data-id="element-3380" data-mp="4j6q4ykyw8ch" />
                        <span className="text-sm" data-id="element-3381" data-mp="9bgbe1b50k8f">My Wishlist</span>
                        {currentWishlistCount > 0 && <span className="ml-auto bg-[#1E2B55] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-3382" data-mp="e9hwssw1cvn2">
                            {currentWishlistCount}
                          </span>}
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-t border-gray-200 pb-32" data-id="element-3383" data-mp="2q03p1ryd5jwi">
                  <button onClick={() => {
                setShowCategoriesMenu(false);
              }} className="flex items-center text-red-500 font-medium hover:text-red-600 w-full" data-id="element-3384" data-mp="2q1wytg49wk8q">
                    <LogOutIcon className="w-4 h-4 mr-2" data-id="element-3385" data-mp="3se9whns37jy6" />
                    <span className="text-sm" data-id="element-3386" data-mp="a7n803l64grr">Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-3387" data-mp="k5t8cgrbteui" />

      <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onSuccess={handleAuthSuccess} data-id="element-3388" data-mp="tw4co4ex9q1x" />

      <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-3389" data-mp="oymb4z38id3y" />
    </>;
}